import { gql } from '@apollo/client';

// ---------------------------------------------------------------------------
// Fragments
// ---------------------------------------------------------------------------

const APPLICANT_ROW_FIELDS = gql`
  fragment ApplicantRowFields on AiConsoleApplicantRow {
    pipelineEntryId
    jobApplicantId
    candidateId
    fullName
    email
    phone
    callPhone
    currentCompany
    currentTitle
    aiScore
    aiScoreReason
    aiScoredResumeId
    aiScoredAt
    interviewRecommendation
    interviewScore
    status
    needsHumanReason
    assignedRecruiterId
    assignedRecruiterName
    justAdded
    createdAt
  }
`;

const CONSOLE_RESULT_FIELDS = gql`
  ${APPLICANT_ROW_FIELDS}
  fragment ConsoleResultFields on AiConsoleResult {
    totalApplicants
    companies {
      companyId
      companyName
      jobOrders {
        jobOrderId
        jobId
        jobTitle
        city
        stateCode
        aiRecruiterEnabled
        applicantCount
        matchCount
        statusCounts {
          newCount
          sendOutreach
          needsHuman
          assigned
          declined
        }
        applicants {
          ...ApplicantRowFields
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Primary console query — Pipeline tab.
 * Variables: { filter: AiConsoleFilterInput }
 *   filter.search?: string
 *   filter.statuses?: AiPipelineStatusEnum[]
 */
export const AI_RECRUITER_CONSOLE = gql`
  ${CONSOLE_RESULT_FIELDS}
  query AiRecruiterConsole($filter: AiConsoleFilterInput) {
    aiRecruiterConsole(filter: $filter) {
      ...ConsoleResultFields
    }
  }
`;

/**
 * Needs-human tab query — same shape as the primary console.
 * Variables: { filter: AiConsoleFilterInput }
 */
export const AI_RECRUITER_NEEDS_HUMAN = gql`
  ${CONSOLE_RESULT_FIELDS}
  query AiRecruiterNeedsHuman($filter: AiConsoleFilterInput) {
    aiRecruiterNeedsHuman(filter: $filter) {
      ...ConsoleResultFields
    }
  }
`;

/**
 * Completion tab — assigned-to-recruiter + declined-by-admin.
 */
export const AI_RECRUITER_COMPLETION = gql`
  ${CONSOLE_RESULT_FIELDS}
  query AiRecruiterCompletion($filter: AiConsoleFilterInput) {
    aiRecruiterCompletion(filter: $filter) {
      ...ConsoleResultFields
    }
  }
`;

/**
 * Fetch assignable recruiters for AssignRecruiterModal.
 * No variables.
 */
export const AI_ASSIGNABLE_RECRUITERS = gql`
  query AiRecruiterAssignableRecruiters {
    aiRecruiterAssignableRecruiters {
      id
      firstName
      lastName
      fullName
      status
    }
  }
`;

/**
 * Fetch action trail for ActionTrailModal.
 * Variables: { pipelineEntryId: ID! }
 */
export const AI_PIPELINE_TRAIL = gql`
  query AiPipelineTrail($pipelineEntryId: ID!) {
    aiPipelineTrail(pipelineEntryId: $pipelineEntryId) {
      id
      pipelineEntryId
      fromStatus
      toStatus
      reason
      detail
      actorUserId
      actorUser {
        id
        fullName
      }
      createdAt
    }
  }
`;

/**
 * Fetch the latest interview result for InterviewResultModal.
 * Variables: { pipelineEntryId: ID! }
 * Returns null when no interview result exists for the entry.
 */
export const AI_INTERVIEW_RESULT = gql`
  query AiInterviewResult($pipelineEntryId: ID!) {
    aiInterviewResult(pipelineEntryId: $pipelineEntryId) {
      id
      pipelineEntryId
      recommendation
      score
      summary
      transcript
      recordingUrl
      sentiment
      callSuccessful
      inVoicemail
      createdAt
    }
  }
`;

/**
 * Poll batch status for IntakeDropzone.
 * Variables: { batchId: ID! }
 */
export const AI_INTAKE_BATCH_STATUS = gql`
  query AiIntakeBatchStatus($batchId: ID!) {
    aiIntakeBatchStatus(batchId: $batchId) {
      fileId
      fileName
      status
      reason
      pipelineEntryId
      jobApplicantId
    }
  }
`;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const RECRUITER_RECORD_FIELDS = gql`
  ${APPLICANT_ROW_FIELDS}
  fragment RecruiterRecordFields on AiRecruiterRecord {
    row {
      ...ApplicantRowFields
    }
  }
`;

/**
 * Toggle company AI recruiter eligibility.
 * Variables: { input: SetEligibilityInput! }
 *   input.id: ID!
 *   input.enabled: Boolean!
 */
export const SET_COMPANY_AI_ELIGIBILITY = gql`
  mutation SetCompanyAiRecruiterEligibility($input: SetEligibilityInput!) {
    setCompanyAiRecruiterEligibility(input: $input) {
      id
      aiRecruiterEnabled
    }
  }
`;

/**
 * Toggle job order AI recruiter eligibility.
 * Variables: { input: SetEligibilityInput! }
 *   input.id: ID!
 *   input.enabled: Boolean!
 */
export const SET_JOB_ORDER_AI_ELIGIBILITY = gql`
  mutation SetJobOrderAiRecruiterEligibility($input: SetEligibilityInput!) {
    setJobOrderAiRecruiterEligibility(input: $input) {
      id
      aiRecruiterEnabled
    }
  }
`;

/**
 * Bulk set status → Send outreach.
 * Variables: { input: BulkPipelineActionInput! }
 *   input.pipelineEntryIds: [ID!]!
 *   input.detail?: String
 */
export const AI_BULK_SEND_OUTREACH = gql`
  ${RECRUITER_RECORD_FIELDS}
  mutation AiBulkSendOutreach($input: BulkPipelineActionInput!) {
    aiBulkSendOutreach(input: $input) {
      ...RecruiterRecordFields
    }
  }
`;

/**
 * Bulk set status → Declined by admin.
 * Variables: { input: BulkPipelineActionInput! }
 *   input.pipelineEntryIds: [ID!]!
 *   input.detail?: String
 */
export const AI_BULK_DECLINE = gql`
  ${RECRUITER_RECORD_FIELDS}
  mutation AiBulkDecline($input: BulkPipelineActionInput!) {
    aiBulkDecline(input: $input) {
      ...RecruiterRecordFields
    }
  }
`;

/**
 * Bulk assign to recruiter.
 * Variables: { input: AssignRecruiterInput! }
 *   input.pipelineEntryIds: [ID!]!
 *   input.recruiterUserId: ID!
 */
export const AI_BULK_ASSIGN = gql`
  ${RECRUITER_RECORD_FIELDS}
  mutation AiBulkAssignToRecruiter($input: AssignRecruiterInput!) {
    aiBulkAssignToRecruiter(input: $input) {
      ...RecruiterRecordFields
    }
  }
`;

/**
 * Undo last reversible pipeline action.
 * Variables: { pipelineEntryId: ID! }
 */
export const AI_UNDO_ACTION = gql`
  ${RECRUITER_RECORD_FIELDS}
  mutation AiUndoPipelineAction($pipelineEntryId: ID!) {
    aiUndoPipelineAction(pipelineEntryId: $pipelineEntryId) {
      ...RecruiterRecordFields
    }
  }
`;

/**
 * Quick-edit a candidate's primary email / phone from a console row.
 */
export const AI_QUICK_EDIT_CONTACT = gql`
  mutation AiQuickEditCandidateContact($input: QuickEditContactInput!) {
    aiQuickEditCandidateContact(input: $input) {
      candidateId
      fullName
      email
      phone
      currentCompany
      currentTitle
    }
  }
`;
